import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Paper,
  IconButton,
  Divider,
  FormHelperText,
  FormControlLabel,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  CalendarMonth as CalendarIcon,
  Event as EventIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  AccessTime as TimeIcon,
  AttachMoney as MoneyIcon,
  ExpandMore as ExpandMoreIcon,
  TableRestaurant as TableIcon,
  Chair as ChairIcon,
  BabyChangingStation as PlaypenIcon,
  Toys as ToysIcon,
  Receipt as ReceiptIcon,
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { eventService, packageService } from '../services/api';
import { Event, Package, EventStatus, EventFormData } from '../types';
import { useEventSettings } from '../hooks/useEventSettings';
import { divertilandiaColors, getCategoryColor } from '../theme/divertilandiaColors';
import dayjs, { Dayjs } from 'dayjs';
import { formatTimeRange, formatTime } from '../utils/dateUtils';

const Calendar: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  const [currentMonth, setCurrentMonth] = useState<Dayjs>(dayjs());
  const [events, setEvents] = useState<Event[]>([]);
  const [packages, setPackages] = useState<Package[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [openEventsList, setOpenEventsList] = useState(false);
  const [selectedDayEvents, setSelectedDayEvents] = useState<Event[]>([]);
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});
  const [openErrorDialog, setOpenErrorDialog] = useState(false);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [formData, setFormData] = useState<EventFormData>({
    contact_name: '',
    contact_phone: '',
    secondary_phone: '',
    address: '',
    google_maps_url: '',
    event_date: dayjs().format('YYYY-MM-DD'),
    start_time: '',
    extra_hours: 0,
    package_id: '',
    status: EventStatus.PENDING,
    child_gender: undefined,
    notes: '',
    
    // Nuevos campos de extras
    has_advance_payment: false,
    advance_payment_amount: 0,
    discount_percentage: 0,
    extra_hours_count: 0,
    extra_tables: 0,
    extra_chairs: 0,
    extra_playpens: 0,
    extra_toys: 0,
    extra_services: '',
    extra_services_cost: 0,
  });

  // Hook para configuraciones de eventos
  const {
    getExtraHourCost,
    getAdvancePaymentAmount,
    getMaxExtraHours,
    loading: settingsLoading,
  } = useEventSettings();

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedDate) {
      fetchEventsForDate(selectedDate.format('YYYY-MM-DD'));
    }
  }, [selectedDate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [eventsData, packagesData] = await Promise.all([
        eventService.getAll(), // Ahora getAll() usa calendar_mode por defecto
        packageService.getAll(),
      ]);
      
      // Validar que los datos sean arrays
      const safeEventsData = Array.isArray(eventsData) ? eventsData : [];
      const safePackagesData = Array.isArray(packagesData) ? packagesData : [];
      
      setEvents(safeEventsData);
      setPackages(safePackagesData);
    } catch (err) {
      setError('Error al cargar los datos');
      // En caso de error, inicializar con arrays vacíos
      setEvents([]);
      setPackages([]);
    } finally {
      setLoading(false);
    }
  };

  // Funciones para calcular costos de extras
  const calculateExtrasCost = (formData: EventFormData) => {
    const extraHourCost = 100; // $100 por hora extra
    const tableSetCost = 100; // $100 por conjunto (mesa + 4 sillas)
    const playpenCost = 200; // $200 por corralito
    
    // Asegurar que todos los valores sean números válidos
    const hoursCount = Number(formData.extra_hours_count) || 0;
    const tablesCount = Number(formData.extra_tables) || 0;
    const playpensCount = Number(formData.extra_playpens) || 0;
    const toysCount = Number(formData.extra_toys) || 0;
    const servicesCost = Number(formData.extra_services_cost) || 0;
    
    const hoursCost = hoursCount * extraHourCost;
    const tablesCost = tablesCount * tableSetCost; // Mesa con 4 sillas = $100
    const playpensCost = playpensCount * playpenCost;
    const toysCost = toysCount * 100; // $100 por juguete extra (a consideración)
    
    const total = hoursCost + tablesCost + playpensCost + toysCost + servicesCost;
    
    return {
      hoursCost,
      tablesCost,
      playpensCost,
      toysCost,
      servicesCost,
      total
    };
  };

  const fetchEventsForDate = async (date: string) => {
    try {
      const eventsData = await eventService.getByDate(date);
      // NO sobrescribir el estado de events aquí, solo usar para debugging
    } catch (err) {
      // Error silencioso para fetchEventsForDate
    }
  };

  const handleDateChange = (newDate: Dayjs | null) => {
    if (newDate) {
      setSelectedDate(newDate);
    }
  };

  const handleOpenDialog = (event?: Event, date?: Dayjs) => {
    const targetDate = date || selectedDate;
    
    if (event) {
      setEditingEvent(event);
      const formattedStartTime = formatTime(event.start_time);
      console.log('🕐 Editando evento - start_time:', {
        original: event.start_time,
        formatted: formattedStartTime,
        eventData: event
      });
      setFormData({
        contact_name: event.contact_name,
        contact_phone: event.contact_phone,
        secondary_phone: event.secondary_phone || '',
        address: event.address,
        google_maps_url: event.google_maps_url || '',
        event_date: extractDateString(event.event_date),
        start_time: formattedStartTime, // Formatear la hora correctamente
        extra_hours: event.extra_hours,
        package_id: event.package_id,
        status: event.status,
        child_gender: event.child_gender,
        notes: event.notes || '',
        
        // Campos de extras (con valores por defecto si no existen)
        has_advance_payment: event.has_advance_payment || false,
        advance_payment_amount: event.advance_payment_amount || 0,
        discount_percentage: event.discount_percentage || 0,
        extra_hours_count: event.extra_hours_count || 0,
        extra_tables: event.extra_tables || 0,
        extra_chairs: event.extra_chairs || 0,
        extra_playpens: event.extra_playpens || 0,
        extra_toys: event.extra_toys || 0,
        extra_services: event.extra_services || '',
        extra_services_cost: event.extra_services_cost || 0,
      });
    } else {
      setEditingEvent(null);
      setFormData({
        contact_name: '',
        contact_phone: '',
        secondary_phone: '',
        address: '',
        google_maps_url: '',
        event_date: targetDate.format('YYYY-MM-DD'),
        start_time: '',
        extra_hours: 0,
        package_id: '',
        status: EventStatus.PENDING,
        child_gender: undefined,
        notes: '',
        
        // Campos de extras con valores por defecto
        has_advance_payment: false,
        advance_payment_amount: 0,
        discount_percentage: 0,
        extra_hours_count: 0,
        extra_tables: 0,
        extra_chairs: 0,
        extra_playpens: 0,
        extra_toys: 0,
        extra_services: '',
        extra_services_cost: 0,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingEvent(null);
    setValidationErrors({});
    setErrorMessages([]);
  };

  const handleOpenEventsList = (date: Dayjs) => {
    const dayEvents = getEventsForDate(date);
    setSelectedDayEvents(dayEvents);
    setSelectedDate(date);
    setOpenEventsList(true);
  };

  const handleCloseEventsList = () => {
    setOpenEventsList(false);
    setSelectedDayEvents([]);
  };

  const validateForm = () => {
    const errors: {[key: string]: string} = {};
    const errorMessages: string[] = [];

    // Validar nombre del cliente
    if (!formData.contact_name.trim()) {
      errors.contact_name = 'El nombre del cliente es obligatorio';
      errorMessages.push('• El nombre del cliente es obligatorio');
    }

    // Validar teléfono principal
    if (!formData.contact_phone.trim()) {
      errors.contact_phone = 'El teléfono principal es obligatorio';
      errorMessages.push('• El teléfono principal es obligatorio');
    }

    // Validar dirección
    if (!formData.address.trim()) {
      errors.address = 'La dirección es obligatoria';
      errorMessages.push('• La dirección del evento es obligatoria');
    }

    // Validar fecha del evento
    if (!formData.event_date) {
      errors.event_date = 'La fecha del evento es obligatoria';
      errorMessages.push('• La fecha del evento es obligatoria');
    }

    // Validar hora de inicio
    if (!formData.start_time) {
      errors.start_time = 'La hora de inicio es obligatoria';
      errorMessages.push('• La hora de inicio es obligatoria');
    }

    // Validar paquete
    if (!formData.package_id) {
      errors.package_id = 'Debe seleccionar un paquete';
      errorMessages.push('• Debe seleccionar un paquete para el evento');
    }

    return { errors, errorMessages };
  };


  const handleSave = async () => {
    try {
      setError(null);
      setValidationErrors({});

      // Validar el formulario
      const { errors, errorMessages } = validateForm();
      
      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        setErrorMessages(errorMessages);
        setOpenErrorDialog(true);
        return;
      }

      // Debug: Log del formData que se va a enviar
      console.log('=== DEBUG FORM DATA ===');
      console.log('FormData completo:', formData);
      console.log('child_gender:', formData.child_gender);
      console.log('========================');

      if (editingEvent) {
        await eventService.update(editingEvent.id, formData);
      } else {
        await eventService.create(formData);
      }
      
      // Actualizar la lista de eventos
      await fetchData();
      handleCloseDialog();
    } catch (err: any) {
      console.error('Error saving event:', err);
      
      // Manejar errores del backend
      if (err.response?.data?.errors) {
        const backendErrors = err.response.data.errors;
        const errorMessages: string[] = [];
        const validationErrors: {[key: string]: string} = {};

        Object.keys(backendErrors).forEach(key => {
          const messages = backendErrors[key];
          if (Array.isArray(messages)) {
            errorMessages.push(...messages.map(msg => `• ${msg}`));
            validationErrors[key] = messages[0];
          }
        });

        setValidationErrors(validationErrors);
        setErrorMessages(errorMessages);
        setOpenErrorDialog(true);
      } else {
        setError('Error al guardar el evento');
      }
    }
  };

  const handleDelete = async (id: string) => {
      try {
        await eventService.delete(id);
      // Actualizar la lista de eventos
      await fetchData();
    } catch (err) {
      setError('Error al eliminar el evento');
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const getStatusColor = (status: EventStatus) => {
    switch (status) {
      case EventStatus.PENDING: return 'warning';
      case EventStatus.CONFIRMED: return 'success';
      case EventStatus.IN_PROGRESS: return 'info';
      case EventStatus.COMPLETED: return 'default';
      case EventStatus.CANCELLED: return 'error';
      default: return 'default';
    }
  };

  const getStatusText = (status: EventStatus) => {
    switch (status) {
      case EventStatus.PENDING: return 'Pendiente';
      case EventStatus.CONFIRMED: return 'Confirmado';
      case EventStatus.IN_PROGRESS: return 'En Progreso';
      case EventStatus.COMPLETED: return 'Completado';
      case EventStatus.CANCELLED: return 'Cancelado';
      default: return status;
    }
  };

  const getEventsForSelectedDate = () => {
    if (!Array.isArray(events)) {
      console.warn('events is not an array:', events);
      return [];
    }
    const targetDate = selectedDate.format('YYYY-MM-DD');
    return events.filter(event => {
      const eventDateStr = extractDateString(event.event_date);
      return eventDateStr === targetDate;
    });
  };

  const getEventsForDate = (date: Dayjs) => {
    if (!Array.isArray(events)) {
      return [];
    }
    const targetDate = date.format('YYYY-MM-DD');
    
    const dayEvents = events.filter(event => {
      const eventDateStr = extractDateString(event.event_date);
      return eventDateStr === targetDate;
    });
    
    return dayEvents;
  };

  const getEventPackageName = (packageId: string) => {
    const pkg = packages.find(p => p.id === packageId);
    return pkg ? pkg.name : 'Sin paquete';
  };

  const getEventStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return '#ff9800'; // Orange
      case 'confirmed': return '#2196f3'; // Blue
      case 'in_progress': return '#9c27b0'; // Purple
      case 'completed': return '#4caf50'; // Green
      case 'cancelled': return '#f44336'; // Red
      default: return '#9e9e9e';
    }
  };

  // Función auxiliar para extraer la fecha en formato YYYY-MM-DD
  const extractDateString = (eventDate: any): string => {
    if (!eventDate) return '';
    
    if (typeof eventDate === 'string') {
      // Si es string, extraer solo la parte de fecha
      return eventDate.split('T')[0];
    } else if (eventDate && typeof eventDate === 'object' && 'toISOString' in eventDate) {
      // Si es Date, convertir a string usando dayjs para evitar problemas de zona horaria
      return dayjs(eventDate).format('YYYY-MM-DD');
    }
    
    return '';
  };

  // Función para formatear precios con máximo 2 decimales
  const formatPrice = (price: number | string | null | undefined) => {
    // Convertir a número y validar
    const numPrice = typeof price === 'string' ? parseFloat(price) : Number(price);
    
    // Si no es un número válido, retornar 0
    if (isNaN(numPrice) || numPrice === null || numPrice === undefined) {
      return '0';
    }
    
    return new Intl.NumberFormat('es-MX', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(numPrice);
  };

  // Función para calcular el precio del paquete con descuento
  const calculatePackagePriceWithDiscount = (packagePrice: number, discountPercentage: number) => {
    const discountAmount = packagePrice * (discountPercentage / 100);
    return packagePrice - discountAmount;
  };

  // Función para obtener el precio total del evento (paquete con descuento + extras)
  const calculateTotalEventPrice = (packagePrice: number, discountPercentage: number, extrasCost: number) => {
    const packagePriceWithDiscount = calculatePackagePriceWithDiscount(packagePrice, discountPercentage);
    return packagePriceWithDiscount + extrasCost;
  };

  // Función para calcular los ingresos del mes por estado
  const getMonthlyRevenueByStatus = () => {
    if (!Array.isArray(events)) return { expected: 0, completed: 0, pending: 0 };
    
    const currentYear = currentMonth.year();
    const currentMonthNumber = currentMonth.month() + 1; // dayjs usa 0-11, necesitamos 1-12
    
    const monthlyEvents = events.filter(event => {
      const eventDateStr = extractDateString(event.event_date);
      if (!eventDateStr) return false;
      
      const eventDate = dayjs(eventDateStr);
      return eventDate.year() === currentYear && (eventDate.month() + 1) === currentMonthNumber;
    });
    
    console.log('=== DEBUG INGRESOS MENSUALES POR ESTADO ===');
    console.log('Mes actual:', currentMonth.format('YYYY-MM'));
    console.log('Eventos del mes:', monthlyEvents.length);
    
    const revenueByStatus = monthlyEvents.reduce((acc, event) => {
      const eventPackage = packages.find(p => p.id === event.package_id);
      
      // Asegurar que los valores sean números
      const packagePrice = Number(eventPackage?.price || 0);
      const discountPercentage = Number(event.discount_percentage || 0);
      const totalExtrasCost = Number(event.total_extras_cost || 0);
      
      // Calcular precio total considerando descuento
      const totalCost = calculateTotalEventPrice(packagePrice, discountPercentage, totalExtrasCost);
      
      console.log(`Evento: ${event.contact_name} - Estado: ${event.status} - Total: $${totalCost}`);
      
      // Clasificar por estado
      if (event.status === 'completed') {
        // Evento completado: todo el monto cuenta como completado
        console.log(`  → Completado: +$${totalCost}`);
        acc.completed += totalCost;
      } else if (event.status === 'cancelled') {
        // Los eventos cancelados no cuentan para ningún ingreso
        console.log(`  → Cancelado: no cuenta`);
        return acc;
      } else if (event.status === 'confirmed') {
        // Evento confirmado: solo el anticipo cuenta como completado, el resto como pendiente
        const advancePayment = Number(event.advance_payment_amount || 0);
        const remainingAmount = totalCost - advancePayment;
        
        console.log(`  → Confirmado: Anticipo $${advancePayment} (completado), Resto $${remainingAmount} (pendiente)`);
        acc.completed += advancePayment;
        acc.pending += remainingAmount;
      } else {
        // pending, in_progress: todo el monto es pendiente
        console.log(`  → ${event.status}: +$${totalCost} (pendiente)`);
        acc.pending += totalCost;
      }
      
      // Todos los eventos (excepto cancelados) cuentan como esperados
      acc.expected += totalCost;
      
      return acc;
    }, { expected: 0, completed: 0, pending: 0 });
    
    console.log('Ingresos calculados:', revenueByStatus);
    console.log('==========================================');
    
    return revenueByStatus;
  };

  // Función para generar el calendario mensual
  const generateCalendarDays = () => {
    const startOfMonth = currentMonth.startOf('month');
    const endOfMonth = currentMonth.endOf('month');
    const startOfCalendar = startOfMonth.startOf('week');
    const endOfCalendar = endOfMonth.endOf('week');
    
    const days = [];
    let day = startOfCalendar;
    
    while (day.isBefore(endOfCalendar) || day.isSame(endOfCalendar, 'day')) {
      days.push(day);
      day = day.add(1, 'day');
    }
    
    return days;
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentMonth(prev => 
      direction === 'prev' 
        ? prev.subtract(1, 'month')
        : prev.add(1, 'month')
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Cargando calendario...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header del calendario */}
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 0 } }}>
        <Typography 
          variant="h4" 
          sx={{ 
            color: divertilandiaColors.standard.primary,
            fontWeight: 700,
            fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' },
            textAlign: { xs: 'center', sm: 'left' }
          }}
        >
          Calendario de Eventos
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{ 
            borderRadius: 2,
            width: { xs: '100%', sm: 'auto' },
            fontSize: { xs: '0.875rem', sm: '1rem' }
          }}
        >
          Nuevo Evento
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Navegación del mes */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <IconButton onClick={() => navigateMonth('prev')}>
            <NavigateBeforeIcon />
          </IconButton>
          <Typography variant="h5" sx={{ 
            color: divertilandiaColors.standard.primary,
            fontWeight: 600 
          }}>
            {currentMonth.format('MMMM YYYY').toUpperCase()}
            </Typography>
          <IconButton onClick={() => navigateMonth('next')}>
            <NavigateNextIcon />
          </IconButton>
        </Box>
        
        {/* Leyenda de estados */}
        <Box display="flex" justifyContent="center" gap={2} sx={{ mt: 2, flexWrap: 'wrap' }}>
          {[
            { status: 'pending', label: 'Pendiente', color: '#ff9800' },
            { status: 'confirmed', label: 'Confirmado', color: '#2196f3' },
            { status: 'in_progress', label: 'En Progreso', color: '#9c27b0' },
            { status: 'completed', label: 'Completado', color: '#4caf50' },
            { status: 'cancelled', label: 'Cancelado', color: '#f44336' },
          ].map((item) => (
            <Box key={item.status} display="flex" alignItems="center" gap={0.5}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: item.color,
                  border: '1px solid rgba(0,0,0,0.1)',
                }}
              />
              <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 500 }}>
                {item.label}
              </Typography>
            </Box>
          ))}
        </Box>
        
        {/* Ingresos del mes por estado */}
        <Box display="flex" justifyContent="center" sx={{ mt: 2 }}>
          <Grid container spacing={2} sx={{ maxWidth: 800 }}>
            {/* Ingreso Esperado */}
            <Grid item xs={12} sm={4}>
              <Box 
                sx={{ 
                  p: 2, 
                  backgroundColor: 'rgba(33, 150, 243, 0.1)', 
                  borderRadius: 2,
                  border: '2px solid rgba(33, 150, 243, 0.3)',
                  textAlign: 'center',
                  minHeight: 120,
                }}
              >
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: 'text.secondary', 
                    fontWeight: 600,
                    mb: 0.5,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontSize: '0.75rem'
                  }}
                >
                  Ingreso Esperado
                </Typography>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    color: '#2196f3',
                    fontWeight: 'bold',
                    fontSize: { xs: '1.2rem', sm: '1.4rem', md: '1.6rem' },
                    textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  }}
                >
                  ${formatPrice(getMonthlyRevenueByStatus().expected)}
                </Typography>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: 'text.secondary',
                    fontSize: '0.7rem',
                    fontStyle: 'italic'
                  }}
                >
                  Total programado
                </Typography>
              </Box>
            </Grid>

            {/* Ingreso Completado */}
            <Grid item xs={12} sm={4}>
              <Box 
                sx={{ 
                  p: 2, 
                  backgroundColor: 'rgba(76, 175, 80, 0.1)', 
                  borderRadius: 2,
                  border: '2px solid rgba(76, 175, 80, 0.3)',
                  textAlign: 'center',
                  minHeight: 120,
                }}
              >
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: 'text.secondary', 
                    fontWeight: 600,
                    mb: 0.5,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontSize: '0.75rem'
                  }}
                >
                  Ingreso Completado
                </Typography>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    color: '#4caf50',
                    fontWeight: 'bold',
                    fontSize: { xs: '1.2rem', sm: '1.4rem', md: '1.6rem' },
                    textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  }}
                >
                  ${formatPrice(getMonthlyRevenueByStatus().completed)}
                </Typography>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: 'text.secondary',
                    fontSize: '0.7rem',
                    fontStyle: 'italic'
                  }}
                >
                  Eventos finalizados
                </Typography>
              </Box>
            </Grid>

            {/* Ingreso Pendiente */}
            <Grid item xs={12} sm={4}>
              <Box 
                sx={{ 
                  p: 2, 
                  backgroundColor: 'rgba(255, 152, 0, 0.1)', 
                  borderRadius: 2,
                  border: '2px solid rgba(255, 152, 0, 0.3)',
                  textAlign: 'center',
                  minHeight: 120,
                }}
              >
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: 'text.secondary', 
                    fontWeight: 600,
                    mb: 0.5,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontSize: '0.75rem'
                  }}
                >
                  Ingreso Pendiente
                </Typography>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    color: '#ff9800',
                    fontWeight: 'bold',
                    fontSize: { xs: '1.2rem', sm: '1.4rem', md: '1.6rem' },
                    textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  }}
                >
                  ${formatPrice(getMonthlyRevenueByStatus().pending)}
                </Typography>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: 'text.secondary',
                    fontSize: '0.7rem',
                    fontStyle: 'italic'
                  }}
                >
                  Por cobrar
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
          </Paper>

      {/* Calendario mensual */}
          <Paper sx={{ p: 2 }}>
        {/* Días de la semana */}
        <Grid container spacing={0} sx={{ mb: 1 }}>
          {['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'].map((day) => (
            <Grid item xs key={day}>
              <Box
                sx={{
                  p: { xs: 1, sm: 1.5, md: 2 },
                  textAlign: 'center',
                  fontWeight: 600,
                  color: divertilandiaColors.standard.primary,
                  backgroundColor: 'grey.50',
                  border: '1px solid',
                  borderColor: 'grey.200',
                  fontSize: { xs: '0.75rem', sm: '0.85rem', md: '1rem' },
                }}
              >
                {day}
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Días del calendario organizados por semanas */}
        {(() => {
          const calendarDays = generateCalendarDays();
          const weeks = [];
          for (let i = 0; i < calendarDays.length; i += 7) {
            weeks.push(calendarDays.slice(i, i + 7));
          }
          
          
          return weeks.map((week, weekIndex) => (
            <Grid container spacing={0} key={weekIndex} sx={{ borderBottom: '1px solid', borderColor: 'grey.200' }}>
              {week.map((day, dayIndex) => {
                const dayEvents = getEventsForDate(day);
                const isCurrentMonth = day.isSame(currentMonth, 'month');
                const isToday = day.isSame(dayjs(), 'day');
                const isSelected = day.isSame(selectedDate, 'day');
                
                
                return (
                  <Grid item xs key={dayIndex} sx={{ borderRight: '1px solid', borderColor: 'grey.200' }}>
                    <Box
                      sx={{
                        minHeight: { xs: 120, sm: 150, md: 180 }, // Responsivo: más pequeño en móvil
                        p: { xs: 0.8, sm: 1, md: 1.2 }, // Padding responsivo
                        backgroundColor: isSelected 
                          ? divertilandiaColors.backgrounds.light.blue
                          : isToday 
                            ? divertilandiaColors.backgrounds.light.green
                            : dayEvents.length > 0
                              ? '#f8f9ff' // Fondo ligeramente azul para días con eventos
                              : 'white',
                        cursor: 'pointer',
                        position: 'relative',
                        border: dayEvents.length > 0 
                          ? '2px solid #e3f2fd' 
                          : '1px solid transparent',
                        '&:hover': {
                          backgroundColor: isCurrentMonth 
                            ? divertilandiaColors.backgrounds.light.blue
                            : 'grey.50',
                          border: dayEvents.length > 0 
                            ? '2px solid #bbdefb' 
                            : '1px solid #e0e0e0',
                        },
                      }}
                      onClick={() => {
                        setSelectedDate(day);
                        if (isCurrentMonth) {
                          if (dayEvents.length > 0) {
                            // Si hay eventos, abrir la lista de eventos del día
                            handleOpenEventsList(day);
                          } else {
                            // Si no hay eventos, abrir formulario para crear nuevo evento
                            handleOpenDialog(undefined, day);
                          }
                        }
                      }}
                    >
                      <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: isToday ? 700 : (dayEvents.length > 0 ? 600 : 400),
                            color: isCurrentMonth 
                              ? isToday 
                                ? divertilandiaColors.standard.primary
                                : dayEvents.length > 0
                                  ? '#1976d2' // Azul más intenso para días con eventos
                                  : 'text.primary'
                              : 'text.disabled',
                            fontSize: { 
                              xs: dayEvents.length > 0 ? '0.8rem' : '0.75rem', // Más pequeño en móvil
                              sm: dayEvents.length > 0 ? '0.9rem' : '0.8rem',
                              md: dayEvents.length > 0 ? '0.95rem' : '0.875rem' 
                            },
                          }}
                        >
                          {day.format('D')}
            </Typography>
            
                        {/* Indicador de eventos del día */}
                        {dayEvents.length > 0 && (
                          <Box
                            sx={{
                              width: { xs: 8, sm: 9, md: 10 }, // Más pequeño en móvil
                              height: { xs: 8, sm: 9, md: 10 },
                              borderRadius: '50%',
                              backgroundColor: dayEvents.length === 1 
                                ? getEventStatusColor(dayEvents[0].status)
                                : '#666',
                              border: { xs: '1px solid white', sm: '1.5px solid white', md: '2px solid white' },
                              boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {dayEvents.length > 1 && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'white',
                                  fontSize: { xs: '0.4rem', sm: '0.45rem', md: '0.5rem' },
                                  fontWeight: 'bold',
                                  lineHeight: 1,
                                }}
                              >
                                {dayEvents.length}
                </Typography>
                            )}
              </Box>
                        )}
                      </Box>
                      
                      {/* Eventos del día - Vista Responsiva */}
                      <Box sx={{ mt: 0.5 }}>
                        {/* Vista móvil - Solo indicadores */}
                        <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                          {dayEvents.slice(0, 2).map((event, eventIndex) => (
                            <Box
                              key={eventIndex}
                              sx={{
                                mb: 0.2,
                                height: 8,
                                backgroundColor: getEventStatusColor(event.status),
                                borderRadius: 0.5,
                                cursor: 'pointer',
                                border: '1px solid rgba(255,255,255,0.3)',
                                '&:hover': {
                                  opacity: 0.8,
                                  transform: 'scale(1.05)',
                                },
                                transition: 'all 0.2s ease',
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenEventsList(day);
                              }}
                            />
                          ))}
                          {dayEvents.length > 2 && (
                            <Box
                              sx={{
                                height: 8,
                                backgroundColor: 'rgba(0,0,0,0.3)',
                                borderRadius: 0.5,
                                cursor: 'pointer',
                                border: '1px dashed rgba(0,0,0,0.3)',
                                '&:hover': {
                                  opacity: 0.8,
                                },
                                transition: 'all 0.2s ease',
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenEventsList(day);
                              }}
                            />
                          )}
                        </Box>

                        {/* Vista tablet/desktop - Información completa */}
                        <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                          {dayEvents.slice(0, 3).map((event, eventIndex) => {
                            const eventPackage = packages.find(p => p.id === event.package_id);
                            
                            const packagePrice = parseFloat(String(eventPackage ? eventPackage.price : 0));
                            const discountPercentage = parseFloat(String(event.discount_percentage || '0'));
                            const totalExtrasCost = parseFloat(String(event.total_extras_cost || '0'));
                            
                            // Calcular precio total considerando descuento
                            const totalCost = calculateTotalEventPrice(packagePrice, discountPercentage, totalExtrasCost);
                            
                            return (
                              <Box
                                key={eventIndex}
                                sx={{
                                  mb: { sm: 0.4, md: 0.5 },
                                  p: { sm: 0.6, md: 0.8 },
                                  backgroundColor: getEventStatusColor(event.status),
                                  borderRadius: { sm: 1, md: 1.2 },
                                  cursor: 'pointer',
                                  border: '1px solid rgba(255,255,255,0.2)',
                                  '&:hover': {
                                    opacity: 0.9,
                                    transform: 'scale(1.02)',
                                  },
                                  transition: 'all 0.2s ease',
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenDialog(event);
                                }}
                              >
                                {/* Hora y Estado */}
                                <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 0.3 }}>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: 'white',
                                      fontSize: { sm: '0.6rem', md: '0.65rem' },
                                      fontWeight: 'bold',
                                      backgroundColor: 'rgba(0,0,0,0.2)',
                                      px: { sm: 0.4, md: 0.5 },
                                      py: { sm: 0.15, md: 0.2 },
                                      borderRadius: 0.5,
                                    }}
                                  >
                                    {formatTimeRange(event.start_time, event.end_time)}
                                  </Typography>
                                  <Box
                                    sx={{
                                      width: { sm: 5, md: 6 },
                                      height: { sm: 5, md: 6 },
                                      borderRadius: '50%',
                                      backgroundColor: 'white',
                                      opacity: 0.8,
                                    }}
                                  />
                                </Box>
                                
                                {/* Cliente */}
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: 'white',
                                    fontSize: { sm: '0.55rem', md: '0.6rem' },
                                    fontWeight: 'bold',
                                    display: 'block',
                                    lineHeight: 1.1,
                                    mb: { sm: 0.25, md: 0.3 },
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {event.contact_name}
                                </Typography>
                                
                                {/* Paquete - MÁS GRANDE */}
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: 'white',
                                    fontSize: { sm: '0.6rem', md: '0.7rem' },
                                    fontWeight: 'bold',
                                    display: 'block',
                                    lineHeight: 1.1,
                                    mb: { sm: 0.25, md: 0.3 },
                                    backgroundColor: 'rgba(255,255,255,0.2)',
                                    px: { sm: 0.4, md: 0.5 },
                                    py: { sm: 0.15, md: 0.2 },
                                    borderRadius: 0.5,
                                    textAlign: 'center',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  📦 {getEventPackageName(event.package_id)}
                                </Typography>
                                
                                {/* Género del niño */}
                                {event.child_gender && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: 'white',
                                      fontSize: { sm: '0.5rem', md: '0.6rem' },
                                      fontWeight: 'bold',
                                      display: 'block',
                                      lineHeight: 1.1,
                                      mb: { sm: 0.25, md: 0.3 },
                                      backgroundColor: 'rgba(255,255,255,0.15)',
                                      px: { sm: 0.3, md: 0.4 },
                                      py: { sm: 0.1, md: 0.15 },
                                      borderRadius: 0.5,
                                      textAlign: 'center',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    👶 {event.child_gender === 'male' ? 'Niño' : 'Niña'}
                                  </Typography>
                                )}
                                
                                {/* Costo Total - MÁS GRANDE Y NOTORIO */}
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: 'white',
                                    fontSize: { sm: '0.65rem', md: '0.75rem' },
                                    fontWeight: 'bold',
                                    display: 'block',
                                    lineHeight: 1.2,
                                    backgroundColor: 'rgba(0,0,0,0.5)',
                                    px: { sm: 0.5, md: 0.6 },
                                    py: { sm: 0.25, md: 0.3 },
                                    borderRadius: 0.5,
                                    textAlign: 'center',
                                    border: '1px solid rgba(255,255,255,0.3)',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                                  }}
                                >
                                  💰 ${formatPrice(totalCost)}
                                </Typography>
                              </Box>
                            );
                          })}
                          
                          {/* Indicador de más eventos */}
                          {dayEvents.length > 3 && (
                            <Box
                              sx={{
                                mt: { sm: 0.25, md: 0.3 },
                                p: { sm: 0.35, md: 0.4 },
                                backgroundColor: 'rgba(0,0,0,0.1)',
                                borderRadius: 1,
                                textAlign: 'center',
                                border: '1px dashed rgba(0,0,0,0.2)',
                              }}
                            >
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  color: 'text.secondary',
                                  fontSize: { sm: '0.5rem', md: '0.55rem' },
                                  fontWeight: 'bold',
                                }}
                              >
                                +{dayEvents.length - 3} eventos más
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>
                          </Box>
                    </Grid>
                  );
                })}
              </Grid>
          ));
        })()}
          </Paper>

      {/* Dialog para crear/editar evento */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
        <DialogTitle>
          {editingEvent ? 'Editar Evento' : 'Nuevo Evento'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Información de Contacto */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ color: divertilandiaColors.standard.primary }}>
                Información de Contacto
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nombre del Cliente"
                value={formData.contact_name}
                onChange={(e) => handleInputChange('contact_name', e.target.value)}
                sx={{ mt: 2, mb: 1 }}
                required
                error={!!validationErrors.contact_name}
                helperText={validationErrors.contact_name}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Teléfono Principal"
                value={formData.contact_phone}
                onChange={(e) => handleInputChange('contact_phone', e.target.value)}
                sx={{ mt: 2, mb: 1 }}
                required
                error={!!validationErrors.contact_phone}
                helperText={validationErrors.contact_phone}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Teléfono Secundario (Opcional)"
                value={formData.secondary_phone}
                onChange={(e) => handleInputChange('secondary_phone', e.target.value)}
                sx={{ mt: 2, mb: 1 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth sx={{ mt: 2, mb: 1 }}>
                <InputLabel>Género del Niño (Opcional)</InputLabel>
                <Select
                  value={formData.child_gender || ''}
                  label="Género del Niño (Opcional)"
                  onChange={(e) => handleInputChange('child_gender', e.target.value)}
                >
                  <MenuItem value="">
                    <em>No especificar</em>
                  </MenuItem>
                  <MenuItem value="male">Niño</MenuItem>
                  <MenuItem value="female">Niña</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
            </Grid>

            {/* Ubicación */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ color: divertilandiaColors.standard.primary }}>
                Ubicación del Evento
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Dirección Completa"
                value={formData.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                sx={{ mt: 2, mb: 1 }}
                multiline
                rows={2}
                required
                error={!!validationErrors.address}
                helperText={validationErrors.address}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="URL de Google Maps (Opcional)"
                value={formData.google_maps_url}
                onChange={(e) => handleInputChange('google_maps_url', e.target.value)}
                sx={{ mt: 2, mb: 1 }}
                placeholder="https://maps.google.com/..."
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
            </Grid>

            {/* Fecha y Hora */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ color: divertilandiaColors.standard.primary }}>
                Fecha y Hora
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Fecha del Evento"
                type="date"
                value={formData.event_date}
                onChange={(e) => handleInputChange('event_date', e.target.value)}
                sx={{ mt: 2, mb: 1 }}
                InputLabelProps={{ shrink: true }}
                required
                error={!!validationErrors.event_date}
                helperText={validationErrors.event_date}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth sx={{ mt: 2, mb: 1 }} required error={!!validationErrors.start_time}>
                <InputLabel>Hora de Inicio</InputLabel>
                <Select
                  value={formData.start_time}
                  onChange={(e) => {
                    console.log('🕐 Select hora cambiada a:', e.target.value);
                    handleInputChange('start_time', e.target.value);
                  }}
                  label="Hora de Inicio"
                  onOpen={() => {
                    console.log('🕐 Select abierto - Valor actual:', formData.start_time);
                  }}
                >
                  {Array.from({ length: 24 }, (_, hour) => {
                    // Crear opciones para hora completa y media hora
                    const times = [
                      { time: `${hour.toString().padStart(2, '0')}:00`, minutes: '00' },
                      { time: `${hour.toString().padStart(2, '0')}:30`, minutes: '30' }
                    ];
                    
                    return times.map(({ time, minutes }) => {
                      const displayTime = hour === 0 ? `12:${minutes} AM` : 
                                        hour < 12 ? `${hour}:${minutes} AM` :
                                        hour === 12 ? `12:${minutes} PM` : 
                                        `${hour - 12}:${minutes} PM`;
                      return (
                        <MenuItem key={time} value={time}>
                          {displayTime}
                        </MenuItem>
                      );
                    });
                  })}
                </Select>
                {validationErrors.start_time && (
                  <FormHelperText error>{validationErrors.start_time}</FormHelperText>
                )}
              </FormControl>
            </Grid>


            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
            </Grid>

            {/* Paquete y Anticipo */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ color: divertilandiaColors.standard.primary }}>
                Paquete y Anticipo
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth sx={{ mt: 2, mb: 1 }} required error={!!validationErrors.package_id}>
                <InputLabel>Paquete</InputLabel>
                <Select
                  value={formData.package_id}
                  onChange={(e) => handleInputChange('package_id', e.target.value)}
                  label="Paquete"
                >
                  {packages.map((pkg) => (
                    <MenuItem key={pkg.id} value={pkg.id}>
                      {pkg.name} - ${formatPrice(pkg.price)}
                    </MenuItem>
                  ))}
                </Select>
                {validationErrors.package_id && (
                  <FormHelperText error>{validationErrors.package_id}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Descuento sobre el Paquete (%)"
                type="number"
                value={formData.discount_percentage}
                onChange={(e) => handleInputChange('discount_percentage', parseFloat(e.target.value))}
                sx={{ mt: 2, mb: 1 }}
                inputProps={{ min: 0, max: 100, step: 0.01 }}
                helperText="Porcentaje de descuento sobre el precio del paquete (0-100%)"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.has_advance_payment}
                      onChange={(e) => handleInputChange('has_advance_payment', e.target.checked)}
                    />
                  }
                  label="¿Dio anticipo?"
                />
                {formData.has_advance_payment && (
              <TextField
                fullWidth
                    label="Monto del anticipo"
                type="number"
                    value={formData.advance_payment_amount}
                    onChange={(e) => handleInputChange('advance_payment_amount', parseFloat(e.target.value))}
                    sx={{ mt: 1 }}
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                }}
              />
                )}
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
            </Grid>

            {/* Extras y Servicios Adicionales */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ color: divertilandiaColors.standard.primary }}>
                Extras y Servicios Adicionales
              </Typography>
            </Grid>

            {/* Horas Extras */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Horas Extras"
                type="number"
                value={formData.extra_hours_count}
                onChange={(e) => handleInputChange('extra_hours_count', parseInt(e.target.value))}
                sx={{ mt: 2, mb: 1 }}
                inputProps={{ min: 0 }}
                helperText="$100 por hora extra"
              />
            </Grid>

            {/* Conjuntos de Mesa y Sillas */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Conjuntos Mesa + 4 Sillas"
                type="number"
                value={formData.extra_tables}
                onChange={(e) => handleInputChange('extra_tables', parseInt(e.target.value))}
                sx={{ mt: 2, mb: 1 }}
                inputProps={{ min: 0 }}
                helperText="$100 por conjunto (mesa + 4 sillas)"
              />
            </Grid>

            {/* Corralitos y Juguetes */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Corralitos"
                type="number"
                value={formData.extra_playpens}
                onChange={(e) => handleInputChange('extra_playpens', parseInt(e.target.value))}
                sx={{ mt: 2, mb: 1 }}
                inputProps={{ min: 0 }}
                helperText="$200 por corralito"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Juguetes Extras"
                type="number"
                value={formData.extra_toys}
                onChange={(e) => handleInputChange('extra_toys', parseInt(e.target.value))}
                sx={{ mt: 2, mb: 1 }}
                inputProps={{ min: 0 }}
                helperText="$100 por juguete (a consideración)"
              />
            </Grid>

            {/* Servicios Adicionales */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Servicios Adicionales"
                value={formData.extra_services}
                onChange={(e) => handleInputChange('extra_services', e.target.value)}
                sx={{ mt: 2, mb: 1 }}
                multiline
                rows={2}
                placeholder="Describe servicios adicionales solicitados..."
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Costo de Servicios Adicionales"
                type="number"
                value={formData.extra_services_cost}
                onChange={(e) => handleInputChange('extra_services_cost', parseFloat(e.target.value))}
                sx={{ mt: 2, mb: 1 }}
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                }}
                placeholder="Costo a consideración"
              />
            </Grid>

            {/* Resumen de Costos */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6" sx={{ color: divertilandiaColors.standard.accent }}>
                    <ReceiptIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Resumen de Costos
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Concepto</TableCell>
                          <TableCell>Cantidad</TableCell>
                          <TableCell>Costo Unitario</TableCell>
                          <TableCell>Total</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {/* Resumen del Paquete */}
                        {formData.package_id && (() => {
                          const selectedPackage = packages.find(p => p.id === formData.package_id);
                          if (!selectedPackage) return null;
                          
                          const packagePrice = selectedPackage.price;
                          const discountPercentage = formData.discount_percentage || 0;
                          const discountAmount = packagePrice * (discountPercentage / 100);
                          const finalPackagePrice = packagePrice - discountAmount;
                          
                          return (
                            <>
                              <TableRow sx={{ backgroundColor: 'grey.50', fontWeight: 'bold' }}>
                                <TableCell>Paquete: {selectedPackage.name}</TableCell>
                                <TableCell>1</TableCell>
                                <TableCell>${formatPrice(packagePrice)}</TableCell>
                                <TableCell>${formatPrice(packagePrice)}</TableCell>
                              </TableRow>
                              {discountPercentage > 0 && (
                                <TableRow sx={{ backgroundColor: '#e8f5e8' }}>
                                  <TableCell colSpan={3} sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
                                    Descuento ({discountPercentage}%)
                                  </TableCell>
                                  <TableCell sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
                                    -${formatPrice(discountAmount)}
                                  </TableCell>
                                </TableRow>
                              )}
                              <TableRow sx={{ backgroundColor: '#f3e5f5', fontWeight: 'bold' }}>
                                <TableCell colSpan={3}>Subtotal Paquete</TableCell>
                                <TableCell sx={{ color: '#7b1fa2', fontWeight: 'bold' }}>
                                  ${formatPrice(finalPackagePrice)}
                                </TableCell>
                              </TableRow>
                            </>
                          );
                        })()}
                        
                        {formData.extra_hours_count > 0 && (
                          <TableRow>
                            <TableCell>Horas Extras</TableCell>
                            <TableCell>{formData.extra_hours_count}</TableCell>
                            <TableCell>$100</TableCell>
                            <TableCell>${formatPrice(formData.extra_hours_count * 100)}</TableCell>
                          </TableRow>
                        )}
                        {formData.extra_tables > 0 && (
                          <TableRow>
                            <TableCell>Conjuntos Mesa + 4 Sillas</TableCell>
                            <TableCell>{formData.extra_tables}</TableCell>
                            <TableCell>$100</TableCell>
                            <TableCell>${formatPrice(formData.extra_tables * 100)}</TableCell>
                          </TableRow>
                        )}
                        {formData.extra_playpens > 0 && (
                          <TableRow>
                            <TableCell>Corralitos</TableCell>
                            <TableCell>{formData.extra_playpens}</TableCell>
                            <TableCell>$200</TableCell>
                            <TableCell>${formatPrice(formData.extra_playpens * 200)}</TableCell>
                          </TableRow>
                        )}
                        {formData.extra_toys > 0 && (
                          <TableRow>
                            <TableCell>Juguetes Extras</TableCell>
                            <TableCell>{formData.extra_toys}</TableCell>
                            <TableCell>$100</TableCell>
                            <TableCell>${formatPrice(formData.extra_toys * 100)}</TableCell>
                          </TableRow>
                        )}
                        {formData.extra_services_cost && formData.extra_services_cost > 0 && (
                          <TableRow>
                            <TableCell>Servicios Adicionales</TableCell>
                            <TableCell>1</TableCell>
                            <TableCell>${formatPrice(formData.extra_services_cost)}</TableCell>
                            <TableCell>${formatPrice(formData.extra_services_cost)}</TableCell>
                          </TableRow>
                        )}
                        <TableRow sx={{ fontWeight: 'bold', backgroundColor: 'grey.50' }}>
                          <TableCell colSpan={3}>Total de Extras</TableCell>
                          <TableCell>${formatPrice(calculateExtrasCost(formData).total)}</TableCell>
                        </TableRow>
                        
                        {/* Total Final */}
                        {formData.package_id && (() => {
                          const selectedPackage = packages.find(p => p.id === formData.package_id);
                          if (!selectedPackage) return null;
                          
                          const packagePrice = selectedPackage.price;
                          const discountPercentage = formData.discount_percentage || 0;
                          const finalPackagePrice = calculatePackagePriceWithDiscount(packagePrice, discountPercentage);
                          const totalExtras = calculateExtrasCost(formData).total;
                          const totalFinal = finalPackagePrice + totalExtras;
                          
                          return (
                            <TableRow sx={{ fontWeight: 'bold', backgroundColor: '#e3f2fd', borderTop: '2px solid #1976d2' }}>
                              <TableCell colSpan={3} sx={{ color: '#1976d2', fontSize: '1.1rem' }}>
                                TOTAL FINAL
                              </TableCell>
                              <TableCell sx={{ color: '#1976d2', fontSize: '1.2rem', fontWeight: 'bold' }}>
                                ${formatPrice(totalFinal)}
                              </TableCell>
                            </TableRow>
                          );
                        })()}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
            </Grid>

            {/* Estado y Notas */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ color: divertilandiaColors.standard.primary }}>
                Estado y Notas
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth sx={{ mt: 2, mb: 1 }}>
                <InputLabel>Estado</InputLabel>
                <Select
                  value={formData.status}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                  label="Estado"
                >
                  <MenuItem value={EventStatus.PENDING}>Pendiente</MenuItem>
                  <MenuItem value={EventStatus.CONFIRMED}>Confirmado</MenuItem>
                  <MenuItem value={EventStatus.IN_PROGRESS}>En Progreso</MenuItem>
                  <MenuItem value={EventStatus.COMPLETED}>Completado</MenuItem>
                  <MenuItem value={EventStatus.CANCELLED}>Cancelado</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notas Adicionales"
                multiline
                rows={3}
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                sx={{ mt: 2, mb: 1 }}
                placeholder="Información adicional sobre el evento..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleSave} variant="contained" disabled={settingsLoading}>
            {editingEvent ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para mostrar lista de eventos del día */}
      <Dialog open={openEventsList} onClose={handleCloseEventsList} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              Eventos del {selectedDate.format('DD/MM/YYYY')}
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                handleCloseEventsList();
                handleOpenDialog(undefined, selectedDate);
              }}
              size="small"
            >
              Nuevo Evento
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedDayEvents.length === 0 ? (
            <Box textAlign="center" py={4}>
              <EventIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No hay eventos para este día
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Haz clic en "Nuevo Evento" para agregar uno
              </Typography>
            </Box>
          ) : (
            <Box>
              {selectedDayEvents.map((event, index) => (
                <Card key={event.id} sx={{ mb: 2, border: `2px solid ${getEventStatusColor(event.status)}` }}>
                  <CardContent>
                    {/* Layout responsivo para móvil y desktop */}
                    <Box>
                      {/* Información principal del evento */}
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="h6" gutterBottom>
                          {event.contact_name}
                        </Typography>
                        <Box display="flex" alignItems="center" sx={{ mb: 1 }}>
                          <TimeIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            {formatTimeRange(event.start_time, event.end_time)}
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" sx={{ mb: 1 }}>
                          <EventIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            {getEventPackageName(event.package_id)}
                          </Typography>
                        </Box>
                        {event.child_gender && (
                          <Box display="flex" alignItems="center" sx={{ mb: 1 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                              👶
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {event.child_gender === 'male' ? 'Niño' : 'Niña'}
                            </Typography>
                          </Box>
                        )}
                        <Box display="flex" alignItems="center" sx={{ mb: 1 }}>
                          <LocationIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary" 
                            sx={{ 
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: { xs: '200px', sm: '300px' }
                            }}
                          >
                            {event.address}
                          </Typography>
                        </Box>
                        <Chip
                          label={getStatusText(event.status)}
                          size="small"
                          sx={{
                            backgroundColor: getEventStatusColor(event.status),
                            color: 'white',
                            fontWeight: 'bold',
                          }}
                        />
                      </Box>
                      
                      {/* Botones de acción - Layout responsivo */}
                      <Box 
                        display="flex" 
                        gap={1}
                        sx={{
                          flexDirection: { xs: 'row', sm: 'row' }, // Siempre en fila
                          justifyContent: { xs: 'center', sm: 'flex-start' }, // Centrado en móvil
                          pt: 1,
                          borderTop: '1px solid',
                          borderColor: 'divider'
                        }}
                      >
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<EditIcon />}
                          onClick={() => {
                            handleCloseEventsList();
                            handleOpenDialog(event);
                          }}
                          sx={{ 
                            backgroundColor: 'primary.main',
                            color: 'white',
                            '&:hover': { backgroundColor: 'primary.dark' },
                            minWidth: { xs: '100px', sm: 'auto' }, // Ancho mínimo en móvil
                            fontSize: { xs: '0.75rem', sm: '0.875rem' }
                          }}
                        >
                          Editar
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<DeleteIcon />}
                          onClick={() => {
                            if (window.confirm('¿Estás seguro de que quieres eliminar este evento?')) {
                              handleDelete(event.id);
                              handleCloseEventsList();
                            }
                          }}
                          sx={{ 
                            backgroundColor: 'error.main',
                            color: 'white',
                            '&:hover': { backgroundColor: 'error.dark' },
                            minWidth: { xs: '100px', sm: 'auto' }, // Ancho mínimo en móvil
                            fontSize: { xs: '0.75rem', sm: '0.875rem' }
                          }}
                        >
                          Eliminar
                        </Button>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEventsList}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Errores de Validación */}
      <Dialog open={openErrorDialog} onClose={() => setOpenErrorDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ color: 'error.main', display: 'flex', alignItems: 'center' }}>
          <Alert severity="error" sx={{ mr: 2, p: 0, backgroundColor: 'transparent' }}>
            ⚠️
          </Alert>
          Error de Validación
        </DialogTitle>
        <DialogContent>
          <Typography variant="h6" gutterBottom>
            No se pudo guardar el evento. Por favor corrige los siguientes errores:
          </Typography>
          <Box sx={{ mt: 2 }}>
            {errorMessages.map((message, index) => (
              <Typography key={index} variant="body1" sx={{ mb: 1, color: 'error.main' }}>
                {message}
              </Typography>
            ))}
          </Box>
          <Alert severity="info" sx={{ mt: 2 }}>
            Los campos marcados en rojo son obligatorios o tienen valores inválidos.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenErrorDialog(false)} variant="contained">
            Entendido
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Calendar;
